import { NOTIFICATION_TIMEOUT_TYPE } from "../../notifications";
import { showWarningNotification } from "../../notifications/actions";
import { timeout } from "../../virtual-background/functions";
import logger from "../../virtual-background/logger";

import JitsiStreamBackgroundEffect from "./JitsiStreamBackgroundEffect";
import createTFLiteModule from "./vendor/tflite/tflite";
import createTFLiteSIMDModule from "./vendor/tflite/tflite-simd";

// The value of modelLandscape is a string that represents the file path or reference to a TFLite model file.
const models = {
    modelLandscape: "libs/selfie_segmentation_landscape.tflite",
};

let modelBuffer;
let tflite;
let wasmCheck;
let isWasmDisabled = false;
// Initializing models landscape with and height
const segmentationDimensions = {
    modelLandscape: {
        height: 144,
        width: 256,
    },
};

let options = {};

/**
 * Creates a new instance of JitsiStreamBackgroundEffect. This loads the Meet background model that is used to
 * extract person segmentation.
 *
 * @param {Object} virtualBackground - The virtual object that contains the background image source and
 * the isVirtualBackground flag that indicates if virtual image is activated.
 * @param {Function} dispatch - The Redux dispatch function.
 * @returns {Promise<JitsiStreamBackgroundEffect>}
 * 

 */

//==============================================================================================================================================================
export async function createEffect(optionsState: Object, dispatch: Function) {
    if (
        !MediaStreamTrack.prototype.getSettings &&
        !MediaStreamTrack.prototype.getConstraints
    ) {
        throw new Error("JitsiStreamBackgroundEffect not supported!");
    }

    // Merging the options object with the optionsState object using the spread operator (...). The result is assigned back to the options variable.
    options = {
        ...options,
        ...optionsState,
    };

    //This block is executed if isWasmDisabled is true. It dispatches a warning notification using the showWarningNotification function and returns.
    if (isWasmDisabled) {
        dispatch(
            showWarningNotification(
                {
                    titleKey: "virtualBackground.backgroundEffectError",
                },
                NOTIFICATION_TIMEOUT_TYPE.LONG
            )
        );

        return;
    }

    // Checks if WebAssembly feature is supported or enabled by/in the browser.
    // Conditional import of wasm-check package is done to prevent
    // the browser from crashing when the user opens the app.
    // This block is executed if tflite is falsy (undefined or null).
    // It attempts to load the TFLite module and sets tflite to the loaded module.

    if (!tflite) {
        try {
            wasmCheck = require("wasm-check");
            const tfliteTimeout = 10000;

            if (wasmCheck?.feature?.simd) {
                tflite = await timeout(tfliteTimeout, createTFLiteSIMDModule());
            } else {
                tflite = await timeout(tfliteTimeout, createTFLiteModule());
            }
        } catch (err) {
            if (err?.message === "408") {
                logger.error("Failed to download tflite model!");
                dispatch(
                    showWarningNotification(
                        {
                            titleKey: "virtualBackground.backgroundEffectError",
                        },
                        NOTIFICATION_TIMEOUT_TYPE.LONG
                    )
                );
            } else {
                isWasmDisabled = true;
                logger.error(
                    "Looks like WebAssembly is disabled or not supported on this browser",
                    err
                );
                dispatch(
                    showWarningNotification(
                        {
                            titleKey: "virtualBackground.webAssemblyWarning",
                            descriptionKey:
                                "virtualBackground.webAssemblyWarningDescription",
                        },
                        NOTIFICATION_TIMEOUT_TYPE.LONG
                    )
                );
            }

            return;
        }
    }
    //This block is executed if modelBuffer is falsy. It fetches the TFLite model file using the path specified in models.modelLandscape.
    if (!modelBuffer) {
        const modelResponse = await fetch(models.modelLandscape);

        if (!modelResponse.ok) {
            throw new Error("Failed to download tflite model!");
        }

        modelBuffer = await modelResponse.arrayBuffer();

        tflite.HEAPU8.set(
            new Uint8Array(modelBuffer),
            tflite._getModelBufferMemoryOffset()
        );

        tflite._loadModel(modelBuffer.byteLength);
    }

    // Deciding whether to run Face Recognition Effect or Virtual Background or two fo them

    if (options.backgroundEffectEnabled !== undefined) {
        options = {
            ...options,
            ...segmentationDimensions.modelLandscape,
        };
    }
    if (options.enabled !== undefined) {
        options = {
            ...options,
            faceRecogEnabled: options.enabled,
        };
    }
    // This returns a new instance of JitsiStreamBackgroundEffect with tflite and options as constructor arguments.
    return new JitsiStreamBackgroundEffect(tflite, options);
}
